import { useEffect, useMemo, useState } from "react"
import { Button, Select } from "antd"
import { Picker } from "antd-mobile"
import { ChevronDownIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { useIsPickerSelectMode } from "@/hooks/use-picker-select-mode"

export interface ResponsiveSelectOption {
  label: string
  value: string
}

export interface ResponsiveSelectProps {
  value?: string
  onChange?: (value?: string) => void
  options: ResponsiveSelectOption[]
  placeholder: string
  allowClear?: boolean
  disabled?: boolean
  loading?: boolean
  className?: string
}

export function ResponsiveSelect({
  value,
  onChange,
  options,
  placeholder,
  allowClear = false,
  disabled = false,
  loading = false,
  className,
}: ResponsiveSelectProps) {
  const isPickerMode = useIsPickerSelectMode()
  const [open, setOpen] = useState(false)
  const [draftValue, setDraftValue] = useState<string | undefined>(value)

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value]
  )

  useEffect(() => {
    if (open) {
      setDraftValue(value)
    }
  }, [open, value])

  if (!isPickerMode) {
    return (
      <Select
        value={value}
        onChange={(nextValue) => onChange?.(nextValue)}
        options={options}
        placeholder={placeholder}
        allowClear={allowClear}
        disabled={disabled}
        loading={loading}
        showSearch
        optionFilterProp="label"
        className={className}
      />
    )
  }

  const handleConfirm = () => {
    onChange?.(draftValue)
    setOpen(false)
  }
  const pickerColumns = useMemo(
    () => [
      (
        allowClear
          ? [{ label: "清空选择", value: "" }, ...options]
          : options
      ).map((option) => ({
        label: option.label,
        value: option.value,
      })),
    ],
    [allowClear, options]
  )

  const pickerValue = useMemo(
    () => [draftValue ?? (allowClear ? "" : options[0]?.value ?? "")],
    [allowClear, draftValue, options]
  )

  return (
    <>
      <Button
        type="default"
        onClick={() => setOpen(true)}
        disabled={disabled || loading}
        className={cn("flex w-full items-center justify-between gap-2", className)}
      >
        <span className={cn("truncate", !selectedOption && "text-muted-foreground")}>
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
      </Button>
      <Picker
        visible={open}
        columns={pickerColumns}
        value={pickerValue}
        title={placeholder}
        cancelText="取消"
        confirmText="确认"
        closeOnMaskClick
        onClose={() => setOpen(false)}
        onCancel={() => setOpen(false)}
        onSelect={(values) => {
          const nextValue = values[0]
          setDraftValue(
            nextValue === undefined || nextValue === "" ? undefined : String(nextValue)
          )
        }}
        onConfirm={(values) => {
          const nextValue = values[0]
          if (!nextValue || nextValue === "") {
            onChange?.(undefined)
          } else {
            onChange?.(String(nextValue))
          }
          setOpen(false)
        }}
        popupClassName="lead-responsive-picker"
      />
    </>
  )
}

export default ResponsiveSelect
